import { useState } from 'react';
import { type ScoringItem, type Criterion, type ScoreMatrix, type ScoringScale } from '../types';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { isScoringComplete } from '../utils/scoring';
import { CriterionReadingHint } from '../../../components/prioritization/values/CriterionReadingHint';

interface ScoringStepProps {
  items: ScoringItem[];
  criteria: Criterion[];
  scoreMatrix: ScoreMatrix;
  scale: ScoringScale;
  onScoreChange: (itemId: string, criterionId: string, score: number) => void;
  onNext: () => void;
}

export function ScoringStep({
  items,
  criteria,
  scoreMatrix,
  scale,
  onScoreChange,
  onNext,
}: ScoringStepProps) {
  const [scoringMode, setScoringMode] = useState<'item' | 'criterion'>('criterion');

  const isComplete = isScoringComplete(items, criteria, scoreMatrix);

  const getScore = (itemId: string, criterionId: string): number | '' => {
    return scoreMatrix[itemId]?.[criterionId] ?? '';
  };

  const handleScoreChange = (itemId: string, criterionId: string, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= scale.min && numValue <= scale.max) {
      onScoreChange(itemId, criterionId, numValue);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
              Score Each Item
            </h2>
            <p className="text-sm text-[var(--text-sec)]">
              Score each item on each criterion using the {scale.min}-{scale.max} scale.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setScoringMode('item')}
              className={`px-3 py-1 text-sm  transition-colors ${
                scoringMode === 'item'
                  ? 'bg-[var(--slate)] text-white'
                  : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
              }`}
            >
              By Item
            </button>
            <button
              onClick={() => setScoringMode('criterion')}
              className={`px-3 py-1 text-sm  transition-colors ${
                scoringMode === 'criterion'
                  ? 'bg-[var(--slate)] text-white'
                  : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
              }`}
            >
              By Criterion
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-[var(--bg-raised)] border border-[var(--rule)] px-2 sm:px-4 py-2 text-left text-sm font-semibold text-[var(--text-sec)] min-w-[96px] sm:min-w-[200px]">
                  Item
                </th>
                {criteria.map((criterion) => (
                  <th
                    key={criterion.id}
                    className="border border-[var(--rule)] px-3 py-2 text-center text-sm font-semibold text-[var(--text-sec)] bg-[var(--surface)]"
                  >
                    <div className="max-w-[120px]">
                      <div className="inline-flex items-center gap-1 font-semibold">
                        {criterion.name}
                        {/* What the criterion measures, when it is one of the workspace's (its id is the attribute key). */}
                        <CriterionReadingHint attributeKey={criterion.id} variant="mark" />
                      </div>
                      <div className="text-xs font-normal text-[var(--text-ter)] mt-1">
                        {criterion.weight}%
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="sticky left-0 z-10 bg-[var(--bg-raised)] border border-[var(--rule)] px-2 sm:px-4 py-2 text-sm font-medium text-[var(--text-pri)]">
                    {item.name}
                  </td>
                  {criteria.map((criterion) => {
                    const score = getScore(item.id, criterion.id);
                    return (
                      <td key={criterion.id} className="border border-[var(--rule)] px-2 py-2 min-w-[72px]">
                        <input
                          type="number"
                          min={scale.min}
                          max={scale.max}
                          step="1"
                          value={score}
                          onChange={(e) => handleScoreChange(item.id, criterion.id, e.target.value)}
                          placeholder="-"
                          className="w-full px-2 py-1 text-center border border-[var(--rule)] rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`mt-4 p-3  ${
          isComplete
            ? 'bg-[var(--green-dim)] border border-[var(--green)]'
            : 'bg-[var(--mustard-dim)] border border-[var(--mustard)]'
        }`}>
          <div className="flex items-center gap-2">
            {isComplete ? (
              <CheckCircle2 className="h-5 w-5 text-[var(--green)]" />
            ) : (
              <AlertCircle className="h-5 w-5 text-[var(--mustard)]" />
            )}
            <span className={`text-sm font-medium ${
              isComplete ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'
            }`}>
              {isComplete
                ? 'All scores are complete'
                : 'Please fill in all scores before continuing'}
            </span>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-[var(--rule)]">
          <button
            onClick={onNext}
            disabled={!isComplete}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed transition-colors"
          >
            {isComplete ? (
              <>
                Calculate Results
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              'Complete all scores to continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

